import * as React from 'react';
import {SearchDropdown} from '../../../FormHeader/components/Search/SearchDropdown';
import {Search, InjectedSearchProps, SearchProps} from '../../../library';
import {
    CheckBox,
    keyCodes,
    Button,
    Flex,
    Dropdown,
    withDropdownContext
} from '@priority-software/priority-style-react';
import {getLocalizedText, texts} from '../../../../constants/TextConst';
import {observer} from 'mobx-react';
import {SearchOptions, ChooseOptions} from '../../../FieldSearch/components/SearchOptions';
import {MultiSelectProps} from '../../../FieldSearch/components/SearchOptions';
import {Column} from '../../../Form/serializers/column';
import {showSearchQueryInResults} from '../../../../components/Common/FieldSearch/utils/search';
import {Label} from '../../../../components/Common/Label';

const ITEM_HEIGHT = 45;

export interface SearchControlProps extends MultiSelectProps {
    value: string[];
    addValue(value): void;
    onChoose(value, ...args): void;
    autoFocus: boolean;
    placeholder: string;
    setActive(): void;
    unsetActive(): void;
    /** inits Search or Choose */
    initFieldSearch: (query?: string) => Promise<any>;
    /**always inits Search */
    initFieldSearchStrict?: (query?: string) => Promise<any>;
    field: Column;
    onClose?: () => void;
}

const ApplyButton = withDropdownContext(({dropdownContext}) => (
    <Button
        width="regularFixed"
        onClick={e => {
            e.stopPropagation();
            dropdownContext.closeDropdown();
        }}
    >
        {getLocalizedText(texts.ok)}
    </Button>
));

@observer
class SearchSelect extends React.Component<
    SearchProps & InjectedSearchProps & SearchControlProps,
    null
> {
    // attach "Enter" key listener to SearchInput
    // only when displaying the "searchQuery" in results
    componentDidUpdate(prevProps) {
        const isShowSearchQueryInResults = showSearchQueryInResults(
            this.props.searchQuery,
            this.props.isNoResults
        );
        if (
            showSearchQueryInResults(prevProps.searchQuery, prevProps.isNoResults) !==
            isShowSearchQueryInResults
        ) {
            if (isShowSearchQueryInResults) {
                window.addEventListener('keydown', this.handleKeyDown, true);
            } else {
                window.removeEventListener('keydown', this.handleKeyDown, true);
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown, true);
    }

    handleKeyDown = e => {
        const {searchQuery, handleSearchChange, addValue} = this.props;
        switch (e.keyCode) {
            case keyCodes.ENTER:
                addValue(searchQuery);
                handleSearchChange('');
                break;
            default:
                break;
        }
    };

    toggleSearchQuery = () => {
        const {searchQuery, onChoose} = this.props;
        onChoose(searchQuery);
    };

    render() {
        const {
            isValueChecked,
            isLoading,
            isSearchable,
            isScrollable,
            searchQuery,
            handleSearchChange,
            handleOpen,
            title2,
            ignorecase,
            handleIgnoreCaseChange,
            searchFromBeginning,
            handleSearchFromBeginningChange,
            autoFocus,
            placeholder,
            setActive,
            unsetActive,
            selectedIndex,
            focusedIndex,
            initFieldSearch,
            direction,
            onClose
        } = this.props;

        const isShowSearchQueryInResults = showSearchQueryInResults(
            this.props.searchQuery,
            this.props.isNoResults
        );

        return (
            <SearchDropdown
                openingSide="leftSide"
                selectedIndex={selectedIndex}
                focusedIndex={focusedIndex}
                closeOnClear
                isMultiSelect
                circularNavigation={!isSearchable || !isScrollable}
                onClose={onClose}
                inputProps={{
                    value: searchQuery,
                    onChange: handleSearchChange,
                    onReset: () => handleSearchChange('', true),
                    direction: direction,
                    placeholder,
                    autoFocus,
                    onFocus: setActive,
                    onBlur: unsetActive,
                    handleSpreadsheetValues: this.props.handleSpreadsheetValues
                }}
                onOpen={searchQuery => handleOpen(initFieldSearch, searchQuery)}
                handlePageDown={this.props.handlePageDown}
                handlePageUp={this.props.handlePageUp}
            >
                <Dropdown.Content width={(isSearchable || title2) && 528}>
                    {!isLoading && (
                        <React.Fragment>
                            {isShowSearchQueryInResults && (
                                <Dropdown.Item
                                    onClick={this.toggleSearchQuery}
                                    closeOnClick={false}
                                    index={0}
                                >
                                    <CheckBox
                                        checked={isValueChecked && isValueChecked(searchQuery)}
                                    >
                                        <Label textColor="primary-700" skin="subtitle-2">
                                            {getLocalizedText(texts.searchPrefix)}
                                            <bdi className="show-search-query">{`'${searchQuery}'`}</bdi>
                                        </Label>
                                    </CheckBox>
                                </Dropdown.Item>
                            )}
                            {isSearchable ? (
                                <SearchOptions
                                    {...this.props}
                                    startIndex={
                                        showSearchQueryInResults(
                                            this.props.searchQuery,
                                            this.props.isNoResults
                                        )
                                            ? 1
                                            : 0
                                    }
                                    autoHeightMax={
                                        showSearchQueryInResults(
                                            this.props.searchQuery,
                                            this.props.isNoResults
                                        )
                                            ? 4 * ITEM_HEIGHT
                                            : 5 * ITEM_HEIGHT
                                    }
                                />
                            ) : (
                                <ChooseOptions
                                    {...this.props}
                                    startIndex={0}
                                    autoHeightMax={5 * ITEM_HEIGHT}
                                />
                            )}
                            <Flex componentClasses="search-footer-container">
                                {isSearchable && (
                                    <div className="checkboxes-container">
                                        <CheckBox
                                            checked={ignorecase}
                                            onChange={handleIgnoreCaseChange}
                                        >
                                            <Label textColor="secondary-400" skin="caption-2">
                                                {getLocalizedText(texts.ignorecase)}
                                            </Label>
                                        </CheckBox>
                                        <CheckBox
                                            checked={searchFromBeginning}
                                            onChange={handleSearchFromBeginningChange}
                                        >
                                            <Label textColor="secondary-400" skin="caption-2">
                                                {getLocalizedText(texts.searchFromBegining)}
                                            </Label>
                                        </CheckBox>
                                    </div>
                                )}
                                <ApplyButton />
                            </Flex>
                        </React.Fragment>
                    )}
                </Dropdown.Content>
            </SearchDropdown>
        );
    }
}

export const SearchControl = props => <Search {...props} render={SearchSelect} />;
